/**
 * Blog API — response contracts.
 */

import { z } from 'zod'

import { BlogCategorySchema, BlogCategoryWithPostCountSchema } from '../../schemas/entities/blog-category'
import {
  BlogPostBaseSchema,
  BlogPostSchema,
  PublicBlogPostSchema,
  PublicBlogPostSummarySchema
} from '../../schemas/entities/blog-post'

// ============================================
// Public reads
// ============================================

export const ListPublicBlogPostsResponseSchema = z.object({
  data: z.array(PublicBlogPostSummarySchema)
})
export type ListPublicBlogPostsResponse = z.infer<typeof ListPublicBlogPostsResponseSchema>

export const GetPublicBlogPostResponseSchema = PublicBlogPostSchema
export type GetPublicBlogPostResponse = z.infer<typeof GetPublicBlogPostResponseSchema>

// ============================================
// Admin reads
// ============================================

export const BlogPostListItemSchema = BlogPostBaseSchema.extend({
  hasDraft: z.boolean(),
  isPublished: z.boolean(),
  publishedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime()
})
export type BlogPostListItem = z.infer<typeof BlogPostListItemSchema>

export const ListBlogPostsResponseSchema = z.object({
  data: z.array(BlogPostListItemSchema)
})
export type ListBlogPostsResponse = z.infer<typeof ListBlogPostsResponseSchema>

export const GetBlogPostResponseSchema = BlogPostSchema
export type GetBlogPostResponse = z.infer<typeof GetBlogPostResponseSchema>

// ============================================
// Admin writes
// ============================================

export const CreateBlogPostResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string()
})
export type CreateBlogPostResponse = z.infer<typeof CreateBlogPostResponseSchema>

export const UpdateBlogPostDraftResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  /** Null when the update touched only metadata and the post has no DRAFT version. */
  draftPublicId: z.string().nullable()
})
export type UpdateBlogPostDraftResponse = z.infer<typeof UpdateBlogPostDraftResponseSchema>

export const PublishBlogPostResponseSchema = z.object({
  publicId: z.string(),
  publishedVersionPublicId: z.string(),
  publishedAt: z.string().datetime()
})
export type PublishBlogPostResponse = z.infer<typeof PublishBlogPostResponseSchema>

export const UnpublishBlogPostResponseSchema = z.object({
  publicId: z.string(),
  /** The version the published one became — unpublish is publish's inverse, not a delete. */
  draftPublicId: z.string()
})
export type UnpublishBlogPostResponse = z.infer<typeof UnpublishBlogPostResponseSchema>

export const DiscardBlogPostDraftResponseSchema = z.object({
  publicId: z.string(),
  /** The DRAFT this call deleted; null when there was none to discard (idempotent). */
  discardedDraftPublicId: z.string().nullable()
})
export type DiscardBlogPostDraftResponse = z.infer<typeof DiscardBlogPostDraftResponseSchema>

export const BlogAssetsUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  publicUrl: z.string().url(),
  key: z.string()
})
export type BlogAssetsUploadUrlResponse = z.infer<typeof BlogAssetsUploadUrlResponseSchema>

// ============================================
// Categories (SCH-2108)
// ============================================

/** Public: only categories with at least one published post, in sort order. */
export const ListPublicBlogCategoriesResponseSchema = z.object({
  data: z.array(BlogCategorySchema)
})
export type ListPublicBlogCategoriesResponse = z.infer<typeof ListPublicBlogCategoriesResponseSchema>

/** Admin: every category, in sort order, with its total post count (drafts included). */
export const ListBlogCategoriesResponseSchema = z.object({
  data: z.array(BlogCategoryWithPostCountSchema)
})
export type ListBlogCategoriesResponse = z.infer<typeof ListBlogCategoriesResponseSchema>

export const CreateBlogCategoryResponseSchema = BlogCategorySchema
export type CreateBlogCategoryResponse = z.infer<typeof CreateBlogCategoryResponseSchema>

export const UpdateBlogCategoryResponseSchema = BlogCategorySchema
export type UpdateBlogCategoryResponse = z.infer<typeof UpdateBlogCategoryResponseSchema>

/** The full admin list in its new order, so the client seeds its cache instead of refetching. */
export const ReorderBlogCategoriesResponseSchema = ListBlogCategoriesResponseSchema
export type ReorderBlogCategoriesResponse = z.infer<typeof ReorderBlogCategoriesResponseSchema>

export const DeleteBlogCategoryResponseSchema = z.object({
  publicId: z.string()
})
export type DeleteBlogCategoryResponse = z.infer<typeof DeleteBlogCategoryResponseSchema>
